import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { createStudent, type StudentEditable, type StudentHubInvitations } from "./api.js";
import { createTutorPrivateStudent } from "./studentCreate.js";

/**
 * Adding a student the booking system does not know about.
 *
 * Until this existed, students arrived only by import or by being adopted
 * backwards from an Amelia booking — so a school cohort, which is taught
 * entirely outside the booking system, could not be entered at all.
 *
 * Only the name is required. A cohort is often a list of names and parents'
 * addresses on the day it is typed in, and a form that insists on an instrument
 * before it will save is one somebody works around. Tutor-private mode reuses
 * that intentionally small core while withholding the owner-only assignment and
 * school-administration fields.
 */

export type NewStudentMode = "admin" | "tutor-private";

type Field = {
  key: keyof StudentEditable;
  label: string;
  type?: string;
  hint?: string;
};

const PRIVATE_FIELDS: Field[] = [
  { key: "parentName", label: "Parent name" },
  {
    key: "parentEmail",
    label: "Parent email",
    type: "email",
    hint: "Where lesson notes go, and what links a parent's account to this child.",
  },
  { key: "parentPhone", label: "Phone", type: "tel" },
  { key: "studentEmail", label: "Student email", type: "email" },
  { key: "instrument", label: "Instrument" },
];

const ADMIN_FIELDS: Field[] = [
  ...PRIVATE_FIELDS,
  { key: "school", label: "School" },
  { key: "formClass", label: "Form" },
];

export function NewStudent({
  onAdded,
  mode = "admin",
  openRequested = false,
  onDismiss,
}: {
  onAdded: () => void;
  mode?: NewStudentMode;
  /** Open the canonical form when another shell entry point launches it. */
  openRequested?: boolean;
  /** Lets a host consume a one-shot launch hint whenever the form closes. */
  onDismiss?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [isUnder18, setIsUnder18] = useState(true);
  const [inviteToHub, setInviteToHub] = useState(true);
  const [fields, setFields] = useState<Partial<StudentEditable>>({});
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const navigate = useNavigate();
  const tutorPrivate = mode === "tutor-private";

  useEffect(() => {
    if (openRequested) setOpen(true);
  }, [openRequested]);

  function reset() {
    setFullName("");
    setIsUnder18(true);
    setInviteToHub(true);
    setFields({});
    setProblem(null);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setProblem(null);
    try {
      const result = tutorPrivate
        ? await createTutorPrivateStudent({
            fullName,
            isUnder18,
            inviteToHub,
            ...(fields.parentName === undefined ? {} : { parentName: fields.parentName }),
            ...(fields.parentEmail === undefined ? {} : { parentEmail: fields.parentEmail }),
            ...(fields.parentPhone === undefined ? {} : { parentPhone: fields.parentPhone }),
            ...(fields.studentEmail === undefined ? {} : { studentEmail: fields.studentEmail }),
            ...(fields.instrument === undefined ? {} : { instrument: fields.instrument }),
          })
        : await createStudent({
            ...fields,
            fullName,
            isUnder18,
            tutorId: null,
            inviteToHub,
          });
      reset();
      setOpen(false);
      // Consume a global-launch query before pushing the canonical record, so
      // Back returns to the roster rather than reopening a completed form.
      onDismiss?.();
      onAdded();
      // Straight to the one canonical record. The query remembers that this
      // tutor came from the My students scope, so Back returns there without
      // maintaining a second student-detail implementation.
      navigate(
        tutorPrivate
          ? `/students/${encodeURIComponent(result.student.id)}?scope=mine`
          : `/students/${encodeURIComponent(result.student.id)}`,
        { state: { hubAccessNotice: hubAccessCreationNotice(result.hubAccess, inviteToHub, fullName) } },
      );
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not add them.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <div className="row">
        <button type="button" onClick={() => setOpen(true)}>
          {tutorPrivate ? "Add student" : "Add a student"}
        </button>
      </div>
    );
  }

  const visibleFields = tutorPrivate ? PRIVATE_FIELDS : ADMIN_FIELDS;

  return (
    <form className="panel" onSubmit={save}>
      <h2>Add a student</h2>
      <p className="small muted">
        {tutorPrivate
          ? "Add a private student to your teaching list. Only the name is needed; contact details can follow later."
          : "For somebody the booking system does not know about — a school lesson, or a new starter before their first booking. Only the name is needed; the rest can follow."}
      </p>

      {problem && <div className="error small">{problem}</div>}

      <div className="fields">
        <label>
          <span className="small muted">Name</span>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoFocus
            required
          />
        </label>

        {visibleFields.map((field) => (
          <label key={field.key}>
            <span className="small muted">{field.label}</span>
            <input
              type={field.type ?? "text"}
              value={(fields[field.key] as string | null) ?? ""}
              onChange={(e) => setFields({ ...fields, [field.key]: e.target.value })}
              autoCapitalize={field.type === "email" ? "none" : undefined}
              spellCheck={field.type === "email" ? false : undefined}
            />
            {field.hint && <span className="small muted">{field.hint}</span>}
          </label>
        ))}

        {!tutorPrivate && <p className="small muted">After saving, add teachers by instrument on the student's record.</p>}

        <label>
          <span className="small muted">Under 18</span>
          <input
            type="checkbox"
            checked={isUnder18}
            onChange={(e) => setIsUnder18(e.target.checked)}
          />
        </label>
        <label>
          <span className="small muted">If they don't have a Hub account yet, email them an invitation.</span>
          <input
            type="checkbox"
            checked={inviteToHub}
            onChange={(e) => setInviteToHub(e.target.checked)}
          />
        </label>
      </div>

      <div className="row">
        <button type="submit" disabled={saving}>
          {saving ? "Adding…" : "Add"}
        </button>
        <button
          className="quiet"
          type="button"
          onClick={() => {
            reset();
            setOpen(false);
            onDismiss?.();
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function hubAccessCreationNotice(
  access: StudentHubInvitations | undefined,
  inviteRequested: boolean,
  studentName: string,
): string | null {
  if (!access) return null;
  if (access.mailFailed?.length) {
    return access.mailFailed.map((email) =>
      `${studentName} was saved, but the invitation email to ${email} could not be queued. Use Resend on the student's page.`,
    ).join(" ");
  }
  if (access.status === "linked_existing") {
    const email = access.addresses.find((address) => address.state === "linked")?.email;
    return email ? `Linked to ${email}'s existing Hub account.` : "Linked to an existing Hub account.";
  }
  if (access.invited?.length) return `Hub invitation emailed to ${access.invited.join(", ")}.`;
  if (access.status === "needs_contact" && inviteRequested) {
    return "No email address on this record, so nobody could be invited. Add one to give the family Hub access.";
  }
  if (access.status === "conflict") {
    const email = access.addresses.find((address) => address.state === "account_unlinked")?.email;
    return `${email ?? "An email address"} belongs to an account that can't be linked automatically. See Hub access below.`;
  }
  if (inviteRequested && access.invitationsEnabled === false) return "Hub invitations aren't switched on yet.";
  return null;
}
